import parseTrip from 'models/parseTrip';

/**
 * Stamped at parse time so the click and purchase events can tell this block from the main listing:
 * the Enhanced Ecommerce list name comes from the carrier and the direction, not from which block.
 * Here and not in the component — a render-time `map` would hand the list a new array every render.
 */
const AFTER_MIDNIGHT_SECTION_SOURCE = 'after_midnight';

/**
 * The timestamp shape the backend emits, `YYYY-MM-DDTHH:MM:SS` with no offset
 * (`AFTER_MIDNIGHT_TIME_FORMAT` on its side). Anchored on purpose: a partial match would let
 * trailing junk through, and this is also the guarantee that comparing the strings sorts by time.
 */
const DEPARTURE_FORMAT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/;

/**
 * Whether a departure is a real instant, not merely a string of the right shape.
 *
 * `2026-02-30T01:00:00` matches the shape and is not a day that exists, and `24:00:00` is a clock
 * the payload never emits; either would sort among the valid trips and could land first, and the
 * heading and the impression event are both built from that first departure.
 *
 * @param {string} departure - The candidate timestamp.
 * @returns {boolean} Whether it can be trusted.
 */
function isRealDeparture(departure) {
  // `exec` coerces, so a one-element array stringifies into a perfectly valid timestamp and would
  // reach the store as an array. The payload contract is a string.
  if (typeof departure !== 'string') return false;

  const match = DEPARTURE_FORMAT.exec(departure);

  if (!match) return false;

  const [, year, month, day, hours, minutes, seconds] = match.map(Number);

  if (hours > 23 || minutes > 59 || seconds > 59) return false;

  const asDate = new Date(year, month - 1, day);

  return (
    asDate.getFullYear() === year && asDate.getMonth() === month - 1 && asDate.getDate() === day
  );
}

/**
 * Parse and order the after-midnight section of a bus search payload.
 *
 * Written to NEVER throw: it runs inside `processBuses`, next to the main listing, so a malformed
 * section must cost the section and not the page. Anything unusable yields `null`.
 *
 * It also sorts, because the section arrives unordered and deliberately skips `filterTrips`, which
 * is where the main listing gets its sort. Comparing the strings is chronological given the format
 * `isRealDeparture` enforces.
 *
 * @param {Object} afterMidnight - The camelized `after_midnight` key of the payload, if present.
 * @param {Object} catalogs - Catalogs `parseTrip` needs.
 * @param {Object} catalogs.cities - Cities by id.
 * @param {Object} catalogs.terminals - Terminals by id.
 * @param {Object} catalogs.lines - Lines by id.
 * @returns {{trips: Array, windowFrom: (string|null)}|null} The section in the funnel's own shape,
 *   or null when there is none to show.
 */
export default function parseAfterMidnightSection(afterMidnight, { cities, terminals, lines }) {
  if (!afterMidnight || typeof afterMidnight !== 'object' || Array.isArray(afterMidnight)) {
    return null;
  }

  try {
    const incoming = Array.isArray(afterMidnight.trips) ? afterMidnight.trips : [];

    const trips = incoming
      .filter((trip) => {
        try {
          // The shape is checked here and nowhere else: `parseTrip` never reads `departure`, so
          // nothing downstream would catch a bad one, and a bad one sorts ahead of the real ones.
          if (
            trip &&
            trip.originId &&
            trip.destinationId &&
            trip.lineId &&
            isRealDeparture(trip.departure)
          ) {
            parseTrip({ trip, cities, terminals, lines });
            return true;
          }
        } catch (error) {
          console.error(
            'Error parsing after midnight trip (incomplete backend data):',
            trip?.id,
            error,
          );
        }
        return false;
      })
      .sort((a, b) => String(a.departure).localeCompare(String(b.departure)));

    // `null`, never an empty section: "is there a section" is answered once, here.
    if (!trips.length) return null;

    // The funnel's own shape: only `trips` and the window start are ever read.
    return {
      trips: trips.map((trip) => ({ ...trip, sectionSource: AFTER_MIDNIGHT_SECTION_SOURCE })),
      windowFrom: afterMidnight.from ?? null,
    };
  } catch (error) {
    console.error('Error processing after midnight section:', error);
    return null;
  }
}
